import * as React from "react"

import * as Checkbox from "@radix-ui/react-checkbox"
import * as Popover from "@radix-ui/react-popover"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { useScrollLockExemption } from "../../hooks/useScrollLockExemption"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"
import {
  resultCountMessage,
  SearchResultsStatus,
} from "../Select/SearchResultsStatus"

/* ── Variants ─────────────────────────────────────────────── */

const multiSelectTriggerVariants = cva([
  "coval-field flex h-control w-full items-center justify-between rounded-md px-2",
  "text-sm text-coval-text placeholder:text-coval-text-muted",
  "bg-surface-default",
  "transition-colors duration-150",
  "hover:bg-surface-contrast disabled:hover:bg-surface-default",
  "border border-surface-1",
  "disabled:opacity-50",
])

const multiSelectContentVariants = cva([
  "z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md p-1",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

/* ── Types ────────────────────────────────────────────────── */

export interface MultiSelectOption {
  value: string
  label: string
  disabled?: boolean
  testId?: string
  trailingContent?: React.ReactNode
  /**
   * Tags associated with this option. When `searchable` is enabled, options
   * match a broad query against their tags as well as their label, and the
   * `tag:<value>` search prefix filters strictly by tag.
   */
  tags?: string[]
  dataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

export interface MultiSelectGroup {
  label: string
  options: MultiSelectOption[]
}

interface DataAttributes {
  [attributeName: `data-${string}`]: string | undefined
}

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

type MultiSelectBaseProps = VariantProps<typeof multiSelectTriggerVariants> &
  AccessibleName & {
    value?: string[]
    defaultValue?: string[]
    onValueChange?: (value: string[]) => void
    onOpenChange?: (open: boolean) => void
    onSearchChange?: (search: string) => void
    placeholder?: string
    searchable?: boolean
    searchPlaceholder?: string
    error?: boolean
    disabled?: boolean
    className?: string
    /** Applied to the trigger, so a `FormField` label can point at it. */
    id?: string
    "data-testid"?: string
    searchInputTestId?: string
    /**
     * Data attributes applied to the portaled dropdown content. Use this when
     * analytics privacy attributes on a trigger ancestor cannot cross the
     * portal boundary.
     */
    contentDataAttributes?: DataAttributes
    /**
     * Optional content pinned to the bottom of the dropdown, below the option
     * list. Useful for a "create new" action so an empty selector is not a
     * dead end.
     */
    footer?: React.ReactNode
    /**
     * Renders the trigger text from the selected options, in selection order.
     * Selected values with no matching option are left out. Defaults to the
     * labels joined with commas; returning null shows the placeholder.
     */
    renderSummary?: (selected: MultiSelectOption[]) => React.ReactNode
    /**
     * Shown in the dropdown when there are no options to list, such as while
     * they load. Defaults to "No results".
     */
    emptyMessage?: React.ReactNode
  }

type FlatMultiSelectProps = MultiSelectBaseProps & {
  options: MultiSelectOption[]
  groups?: never
}

type GroupedMultiSelectProps = MultiSelectBaseProps & {
  options?: never
  groups: MultiSelectGroup[]
}

export type MultiSelectProps = FlatMultiSelectProps | GroupedMultiSelectProps

/* ── Component ────────────────────────────────────────────── */

function OptionRow({
  option,
  checked,
  onToggle,
}: {
  option: MultiSelectOption
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label
      {...option.dataAttributes}
      data-testid={option.testId}
      className={cn(
        "flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5",
        "text-sm text-coval-text",
        option.disabled
          ? "cursor-default opacity-50"
          : "hover:bg-surface-contrast",
      )}
    >
      <Checkbox.Root
        checked={checked}
        disabled={option.disabled}
        onCheckedChange={onToggle}
        aria-label={option.label}
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded",
          "border border-coval-border-control",
          "transition-colors duration-150",
          checked
            ? "bg-coval-contrast border-coval-contrast"
            : "bg-surface-default",
        )}
      >
        <Checkbox.Indicator>
          <CheckIcon />
        </Checkbox.Indicator>
      </Checkbox.Root>
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
      {option.trailingContent ? (
        <span className="ml-auto shrink-0">{option.trailingContent}</span>
      ) : null}
    </label>
  )
}

function MultiSelect(props: MultiSelectProps) {
  const {
    value: controlledValue,
    defaultValue,
    onValueChange,
    onOpenChange,
    onSearchChange,
    placeholder = "Select...",
    searchable = false,
    searchPlaceholder = "Search...",
    error,
    disabled,
    className,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "data-testid": dataTestId,
    searchInputTestId,
    contentDataAttributes,
    footer,
    renderSummary,
    emptyMessage = "No results",
  } = props

  const isGrouped = "groups" in props && props.groups !== undefined
  const rawGroups = isGrouped ? props.groups : props.options
  const groups = React.useMemo<MultiSelectGroup[]>(
    () =>
      isGrouped
        ? (rawGroups as MultiSelectGroup[])
        : [{ label: "", options: rawGroups as MultiSelectOption[] }],
    [isGrouped, rawGroups],
  )

  const [internalValue, setInternalValue] = React.useState<string[]>(
    defaultValue ?? [],
  )
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const scrollableListRef = useScrollLockExemption<HTMLDivElement>()
  const contentId = React.useId()

  const isControlled = controlledValue !== undefined
  const selected = isControlled ? controlledValue : internalValue

  const allOptions = React.useMemo(
    () => groups.flatMap((g) => g.options),
    [groups],
  )

  const handleToggle = React.useCallback(
    (optionValue: string) => {
      const next = selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue]

      if (!isControlled) {
        setInternalValue(next)
      }
      onValueChange?.(next)
    },
    [selected, isControlled, onValueChange],
  )

  const displayText = React.useMemo<React.ReactNode>(() => {
    if (selected.length === 0) {
      return null
    }
    const selectedOptions = selected.flatMap((v) => {
      const option = allOptions.find((o) => o.value === v)
      return option ? [option] : []
    })
    if (renderSummary) {
      return renderSummary(selectedOptions)
    }
    return selectedOptions.map((option) => option.label).join(", ")
  }, [selected, allOptions, renderSummary])

  const filteredGroups = React.useMemo(() => {
    if (!searchable || search.trim() === "") {
      return groups
    }
    const trimmedQuery = search.trim()

    const matchesTag = (option: MultiSelectOption, tagQuery: string) =>
      (option.tags ?? []).some((tag) => tag.toLowerCase().includes(tagQuery))

    // `tag:<value>` filters strictly by tag (e.g. "tag:nidhi").
    const tagFilterMatch = trimmedQuery.match(/^tag:(.*)$/i)
    const matchesOption = (option: MultiSelectOption) => {
      if (tagFilterMatch) {
        const tagQuery = tagFilterMatch[1].trim().toLowerCase()
        // A bare `tag:` shows everything until a tag value is typed.
        return tagQuery === "" || matchesTag(option, tagQuery)
      }
      const query = trimmedQuery.toLowerCase()
      return (
        option.label.toLowerCase().includes(query) || matchesTag(option, query)
      )
    }

    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter(matchesOption),
      }))
      .filter((group) => group.options.length > 0)
  }, [groups, search, searchable])

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      onOpenChange?.(nextOpen)
      if (!nextOpen) {
        setSearch("")
        onSearchChange?.("")
      }
    },
    [onOpenChange, onSearchChange],
  )

  const hasResults = filteredGroups.some((g) => g.options.length > 0)

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild disabled={disabled}>
        {/* A combobox because its text is the current value, not its name;
            a plain button would be named by that text. */}
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          // The popover's id only resolves while it is open.
          aria-controls={open ? contentId : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          data-testid={dataTestId}
          data-invalid={error || undefined}
          className={cn(
            multiSelectTriggerVariants(),
            error &&
              "border-coval-danger-border hover:border-coval-danger-border focus:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface",
            className,
          )}
        >
          <span className="truncate">
            {displayText ?? (
              <span
                className={
                  error
                    ? "text-coval-danger-on-surface/80"
                    : "text-coval-text-muted"
                }
              >
                {placeholder}
              </span>
            )}
          </span>
          <ChevronDownIcon />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          {...contentDataAttributes}
          id={contentId}
          className={cn(multiSelectContentVariants())}
          // The popover is a dialog; it takes the field's name.
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => {
            if (searchable) {
              e.preventDefault()
              searchInputRef.current?.focus()
            }
          }}
        >
          {searchable && (
            <div className="px-1 pb-1">
              <div className="coval-field flex items-center gap-1.5 rounded-md border border-surface-1 bg-surface-default px-2">
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    onSearchChange?.(e.target.value)
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  data-testid={searchInputTestId}
                  className="h-control w-full bg-transparent text-sm text-coval-text placeholder:text-coval-text-muted"
                />
              </div>
            </div>
          )}
          <div ref={scrollableListRef} className="max-h-60 overflow-y-auto">
            {filteredGroups.map((group, groupIndex) => (
              <div
                key={`${groupIndex}-${group.label}`}
                role={isGrouped ? "group" : undefined}
                aria-label={isGrouped && group.label ? group.label : undefined}
              >
                {isGrouped && groupIndex > 0 && (
                  <div className="mx-1 my-1 border-t border-coval-border" />
                )}
                {isGrouped && group.label && (
                  <span className="px-2 py-1.5 text-xs font-medium text-coval-text-muted">
                    {group.label}
                  </span>
                )}
                {group.options.map((option) => (
                  <OptionRow
                    key={option.value}
                    option={option}
                    checked={selected.includes(option.value)}
                    onToggle={() => handleToggle(option.value)}
                  />
                ))}
              </div>
            ))}
          </div>
          <SearchResultsStatus
            emptyState={
              !hasResults ? (
                <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                  {emptyMessage}
                </p>
              ) : null
            }
            announcement={resultCountMessage(
              search,
              filteredGroups.reduce(
                (total, group) => total + group.options.length,
                0,
              ),
            )}
          />
          {footer ? (
            <div className="mt-1 border-t border-coval-border pt-1">
              {footer}
            </div>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

MultiSelect.displayName = "MultiSelect"

/* ── Inline icons ─────────────────────────────────────────── */

function SearchIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0 text-coval-text-muted"
    >
      <path
        d="M5.5 9.5C7.7091 9.5 9.5 7.7091 9.5 5.5C9.5 3.29086 7.7091 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.7091 3.29086 9.5 5.5 9.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 10.5L8.32 8.32"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-coval-text-on-contrast"
      />
    </svg>
  )
}

/* ── Exports ──────────────────────────────────────────────── */

export { MultiSelect, multiSelectTriggerVariants, multiSelectContentVariants }
